import * as Dialog from "@radix-ui/react-dialog"
import {
  ArrowRightIcon,
  CheckCircledIcon,
  Cross2Icon,
  CrossCircledIcon,
  ReloadIcon,
  ResetIcon,
} from "@radix-ui/react-icons"
import axios from "axios"
import cn from "classnames"
import getConfig from "next/config"
import Script from "next/script"
import { createContext, useContext, useEffect, useRef, useState } from "react"
import Image from "../Image"
import Section, { parseColors } from "../Section"
import LinkButton from "../ui/LinkButton"
import Shape from "../ui/Shape"
import styles from "./ContactForm.module.scss"
import { twCustomConfig } from "utils/tw-config"

const {
  theme: {
    colors: {
      black: { name: defaultBackgroundColor },
      berry: { name: defaultAccentColor },
    },
  },
} = twCustomConfig

const {
  publicRuntimeConfig: { UNIT_SITE },
} = getConfig()

const DEV_MODE = process.env.NODE_ENV === "development"

const RECAPTCHA_SITEKEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITEKEY
const RECAPTCHA_ENABLED =
  process.env.NEXT_PUBLIC_RECAPTCHA_ENABLED === "true" && !DEV_MODE

const RECAPTCHA_TIMEOUT_MS = 15000

// The CMS colors are overwritten for now.
const formColors = [
  {
    name: defaultBackgroundColor,
    value: twCustomConfig.theme.colors[defaultBackgroundColor].DEFAULT,
  },
  {
    name: defaultAccentColor,
    value: twCustomConfig.theme.colors[defaultAccentColor].DEFAULT,
  },
]

const MESSAGE_TRANSLATIONS = [
  [/valid email/i, "Bitte geben Sie eine gültige E-Mail-Adresse ein."],
  [/must be accepted/i, "Bitte stimmen Sie der Datenschutzerklärung zu."],
  [/required/i, "Bitte füllen Sie dieses Feld aus."],
]

const FALLBACK_LABELS = { datenschutz: "Datenschutzerklärung" }

function translateMessage(message) {
  return (
    MESSAGE_TRANSLATIONS.find(([pattern]) => pattern.test(message))?.[1] ??
    message
  )
}

const FormErrorContext = createContext(null)

export const Field = ({
  id,
  label,
  className = "sm:col-span-2",
  contentClassName = "mt-1",
  children,
}) => {
  const { errors, registerLabel } = useContext(FormErrorContext) ?? {}
  const error = errors?.[id]

  useEffect(() => {
    registerLabel?.(id, label)
  }, [id, label, registerLabel])

  return (
    <div className={cn(className, { [styles.fieldError]: error })}>
      <label htmlFor={id}>{label}</label>
      <div className={contentClassName}>{children}</div>
      {error && (
        <p className={styles.errorText} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

function verifyRecaptcha() {
  return new Promise((resolve, reject) => {
    if (!RECAPTCHA_SITEKEY) {
      reject(new Error("reCAPTCHA site key is not configured"))
      return
    }

    if (typeof window.grecaptcha?.ready !== "function") {
      reject(new Error("reCAPTCHA has not loaded"))
      return
    }

    const timeout = setTimeout(
      () => reject(new Error("reCAPTCHA timed out")),
      RECAPTCHA_TIMEOUT_MS
    )

    window.grecaptcha.ready(() => {
      try {
        // `execute` returns Google's own thenable, which lacks `finally`.
        Promise.resolve(
          window.grecaptcha.execute(RECAPTCHA_SITEKEY, { action: "submit" })
        )
          .then((token) =>
            axios.post(
              "/api/contact",
              { recaptchaResponse: token },
              { headers: { "Content-Type": "application/json" } }
            )
          )
          .then(resolve, reject)
          .finally(() => clearTimeout(timeout))
      } catch (err) {
        clearTimeout(timeout)
        reject(err)
      }
    })
  })
}

async function verifyRecaptchaWithRetry() {
  try {
    await verifyRecaptcha()
  } catch (err) {
    if (!err.response?.data?.errors?.includes("browser-error")) {
      throw err
    }
    // Google classifies `browser-error` as retriable; a second token usually works.
    await verifyRecaptcha()
  }
}

const BaseForm = ({
  data,
  content,
  formId,
  action,
  submitLabel,
  phoneRequired = false,
  onReset,
  children,
}) => {
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [privacyChecked, setPrivacyChecked] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [errorList, setErrorList] = useState([])

  const labels = useRef({})
  const registerLabel = useRef((id, label) =>
    Object.assign(labels.current, { [id]: String(label).replace(/\s*\*$/, "") })
  ).current

  const {
    contact_form_headline: headline,
    contact_form_text: text,
    contact_form_contact: contact,
    contact_form_phone: phone,
    ankerslug,
  } = data

  const { accent } = parseColors({ colors: formColors })

  function showErrors(errors) {
    setFieldErrors(errors)
    setErrorList(
      Object.entries(errors).map(([field, message]) => ({
        field,
        label: labels.current[field] ?? FALLBACK_LABELS[field] ?? field,
        message,
      }))
    )
    setError(true)
  }

  function clearFieldError(name) {
    setFieldErrors((current) => {
      if (!(name in current)) {
        return current
      }

      const next = { ...current }
      delete next[name]

      return next
    })
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (loading) {
      return
    }
    setLoading(true)
    setErrorList([])
    setFieldErrors({})

    try {
      if (RECAPTCHA_ENABLED) {
        await verifyRecaptchaWithRetry()
      }
      await sendForm()
    } catch (err) {
      // Statamic reports validation failures as `error: {field: message}`;
      const validationErrors = err.response?.data?.error

      if (validationErrors) {
        showErrors(
          Object.fromEntries(
            Object.entries(validationErrors).map(([field, message]) => [
              field,
              translateMessage(message),
            ])
          )
        )
      } else {
        console.error(err)
        setError(true)
      }
    } finally {
      setLoading(false)
    }
  }

  async function sendForm() {
    const form = document.getElementById(formId)
    await axios.post(form.action, new FormData(form), {
      // does not work with set "content-type: multipart/form-data"
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })

    form.reset()
    setPrivacyChecked(false)
    setFieldErrors({})
    setErrorList([])
    onReset?.()
    setSuccess(true)

    // Tracking if configured on landingpages
    if (content?.enable_meta_tracking) {
      window.fbq?.("track", "Lead")
    }
  }

  return (
    <FormErrorContext.Provider value={{ errors: fieldErrors, registerLabel }}>
      <Section
        id={ankerslug}
        sectionSpacing={{
          top: data?.section_padding_top?.value || null,
          bottom: data?.section_padding_bottom?.value || null,
        }}
        colors={formColors}
      >
        {RECAPTCHA_ENABLED && RECAPTCHA_SITEKEY && (
          <Script
            src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITEKEY}`}
            onLoad={() => {
              DEV_MODE && console.log("recaptcha loaded")
            }}
          />
        )}

        <div
          className={cn(styles.contactWrapper, {
            [styles[`contactForm--${content.locale}`]]: content.locale,
          })}
        >
          <div className={styles.introWrapper}>
            {headline && <h1 className={styles.title}>{headline}</h1>}
            {text && (
              <div
                className={styles.copy}
                dangerouslySetInnerHTML={{ __html: text }}
              />
            )}

            {contact && (
              <div className={styles.imageWrapper}>
                <Shape
                  type={"rect-h-rounded-half"}
                  fill={accent.color}
                  className={styles.imageBG}
                />
                {contact?.avatar?.permalink ? (
                  <div className={"flex-shrink-0 mb-8 w-full sm:w-2/5 z-10"}>
                    <Image
                      className={styles.contactImage}
                      width={416}
                      height={561}
                      src={contact.avatar?.permalink}
                      alt={contact.avatar?.alt || "Kontaktbild"}
                      sizes="(max-width: 639px) 100vw, 192px"
                    />
                  </div>
                ) : null}
                <div className="text-white -mt-10 ml-4 sm:ml-24 lg:ml-28 z-10">
                  {contact?.title ? (
                    <div className="font-semibold">{contact.title}</div>
                  ) : null}
                  {contact?.position ? <div>{contact.position}</div> : null}
                  {phone ? (
                    <div>
                      <a href={`tel:${phone}`}>{phone}</a>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
          <div className={styles.formWrapper}>
            <form
              action={action}
              method="POST"
              className={styles.form}
              onSubmit={handleSubmit}
              onInput={({ target }) => clearFieldError(target.name)}
              id={formId}
            >
              <input
                type="hidden"
                name="unit"
                id="unit"
                defaultValue={UNIT_SITE}
              />

              <div className={styles.formInner}>
                <div className={styles.personalInfo}>
                  <div className={styles.names}>
                    <Field id="first-name" label="Vorname *" className="">
                      <input
                        required
                        type="text"
                        name="first-name"
                        id="first-name"
                        autoComplete="given-name"
                        className={styles.inputFields}
                      />
                    </Field>
                    <Field id="last-name" label="Nachname *" className="">
                      <input
                        required
                        type="text"
                        name="last-name"
                        id="last-name"
                        autoComplete="family-name"
                        className={styles.inputFields}
                      />
                    </Field>
                  </div>
                  <Field id="company" label="Firma *">
                    <input
                      required
                      type="text"
                      name="company"
                      id="company"
                      autoComplete="organization"
                      className={styles.inputFields}
                    />
                  </Field>
                  <Field id="email" label="E-Mail *">
                    <input
                      required
                      id="email"
                      name="email"
                      type="email"
                      // `type="email"` alone accepts domains without a TLD.
                      pattern="[^@\s]+@[^@\s]+\.[A-Za-z]{2,}"
                      title="Bitte eine vollständige E-Mail-Adresse angeben, z. B. name@firma.de"
                      autoComplete="email"
                      className={styles.inputFields}
                    />
                  </Field>
                  <Field
                    id="phone"
                    label={`Telefon ${phoneRequired ? "*" : ""}`.trim()}
                  >
                    <input
                      required={phoneRequired}
                      type="text"
                      name="phone"
                      id="phone"
                      autoComplete="tel"
                      className={styles.inputFields}
                    />
                  </Field>
                </div>

                <div className={styles.contactInfo}>
                  {children}

                  <div
                    className={cn("flex items-baseline sm:col-span-2", {
                      [styles.fieldError]: fieldErrors.datenschutz,
                    })}
                  >
                    <input
                      required
                      id="datenschutz"
                      name="datenschutz"
                      checked={privacyChecked}
                      onChange={({ target }) =>
                        setPrivacyChecked(target.checked)
                      }
                      type="checkbox"
                      className={styles.radioButtons}
                    />
                    <label htmlFor="datenschutz" className="ml-3">
                      <span>
                        Ich habe die{" "}
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="/datenschutzerklaerung"
                        >
                          Datenschutzerklärung
                        </a>{" "}
                        gelesen und erkläre mich damit einverstanden. * <br />
                      </span>
                      {fieldErrors.datenschutz && (
                        <p className={styles.errorText} role="alert">
                          {fieldErrors.datenschutz}
                        </p>
                      )}
                    </label>
                  </div>
                </div>
              </div>
              <div className="text-right sm:col-span-2">
                <button
                  className={cn("button", styles.submitButton, { loading })}
                  aria-label={submitLabel}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      Wird versendet{" "}
                      <ReloadIcon className={"animate-spin"} aria-hidden />
                    </>
                  ) : (
                    <>
                      {submitLabel} <ArrowRightIcon aria-hidden />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <Dialog.Root open={success} onOpenChange={(state) => setSuccess(state)}>
          <Dialog.Portal>
            <Dialog.DialogOverlay className={styles.successModalOverlay} />
            <Dialog.Content
              className={`${styles.modalContent} ${styles.successModalContent}`}
              role="alert"
            >
              <h5 className={cn("mb-2 font-semibold", styles.modalHeadline)}>
                <CheckCircledIcon className={styles.modalIcon} aria-hidden />
                Vielen Dank für Ihre Anfrage!
              </h5>
              <p className="mb-2">
                Wir werden uns in Kürze mit Ihnen in Verbindung setzen.
              </p>
              <LinkButton
                data={{
                  link_text: "Zur Startseite",
                  link_url: "/",
                  link_blank: false,
                  show_arrow: true,
                }}
              />
              <Dialog.Close asChild>
                <div className={styles.iconButton}>
                  <Cross2Icon />
                </div>
              </Dialog.Close>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
        <Dialog.Root open={error} onOpenChange={(state) => setError(state)}>
          <Dialog.Portal>
            <Dialog.DialogOverlay className={styles.errorModalOverlay} />
            <Dialog.Content
              className={`${styles.modalContent} ${styles.errorModalContent}`}
              role="alert"
            >
              <h5 className={cn("mb-2 font-semibold", styles.modalHeadline)}>
                <CrossCircledIcon className={styles.modalIcon} aria-hidden />
                {errorList.length > 0
                  ? "Bitte prüfen Sie Ihre Eingaben"
                  : "Leider ist ein Fehler aufgetreten!"}
              </h5>
              {errorList.length > 0 ? (
                <ul className={styles.errorSummary}>
                  {errorList.map(({ field, label, message }) => (
                    <li key={field}>
                      <b>{label}:</b> {message}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mb-2">
                  Bitte versuchen Sie es später noch einmal.
                </p>
              )}
              <button
                className={cn("button", styles.modalButton)}
                aria-label="Zurück zum Formular"
                onClick={() => setError(false)}
              >
                Zurück zum Formular <ResetIcon aria-hidden />
              </button>
              <Dialog.Close asChild>
                <div className={styles.iconButton}>
                  <Cross2Icon />
                </div>
              </Dialog.Close>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </Section>
    </FormErrorContext.Provider>
  )
}

export default BaseForm
